import { Button, Grid } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import React from 'react';
import ReactFlow from 'react-flow-renderer/nocss';
import { useDispatch, useSelector } from 'react-redux';
import { actions as graphActions } from '../../store/slices/v3Graph';
import {
    areAllNodesVisited,
    getNextNode,
    isEdge,
    processEdge,
    processNode,
} from '../../utils';
import ColorCodes from '../ColorCodes';

import './index.css';
import CustomEdge from '../CustomEdge';
import CustomNode from '../CustomNode';

const V3Graph = () => {
    const dispatch = useDispatch();

    const graph = useSelector((state) => state.v3Graph);
    const elements = graph.nodes
        .map(processNode)
        .concat(graph.edges.map(processEdge));

    const handleElementClick = (event, element) => {
        if (isEdge(element)) {
            return;
        }
        dispatch(
            graphActions.select({
                nodeId: element.id,
            })
        );
    };

    const allNodesVisited = areAllNodesVisited(graph);

    const isNextDisabled = getNextNode(graph) === null;

    const handleNextClick = () => {
        dispatch(graphActions.next());
    };

    return (
        <Grid className="grid-container" container>
            <Grid md={4}>
                <div className="content-container">
                    <p className="title">Objective</p>
                    <p>
                        Given a directed graph with non-negative weights, find
                        the shortest path from the source node to every node in
                        the graph.
                    </p>
                    <ColorCodes />

                    <p className="title">Setup</p>
                    <p>
                        You can click on the
                        <span className="next-button-indicator">NEXT</span>{' '}
                        button.
                    </p>
                    <p>
                        On each click of this button, the system will visit a
                        valid node, and annotate it with the length of the
                        shortest path.
                    </p>
                    <p>
                        You can click on any visited node to set it as the
                        destination node and see the shortest path from the
                        source node.
                    </p>
                    <p className="title">Procedure</p>
                    <p>
                        Step 1: Click the "Next" button and the algorithm will
                        select a suitable node and visit it.
                    </p>
                    <p>
                        Step 2: Once all the nodes are visited, you will not be
                        able to click "Next", indicating the algorithm has
                        terminated.
                    </p>
                </div>
            </Grid>
            <Grid md={8} className="graph-container">
                {allNodesVisited && (
                    <Alert severity="success" className="message">
                        All nodes have been visited
                    </Alert>
                )}
                <Button
                    disabled={isNextDisabled}
                    className="next-button"
                    onClick={handleNextClick}
                    size="large"
                    color="primary"
                    variant="contained"
                >
                    {isNextDisabled ? 'DONE' : 'NEXT'}
                </Button>
                <ReactFlow
                    zoomOnDoubleClick={false}
                    zoomOnScroll={false}
                    paneMoveable={false}
                    onLoad={(f) => f.fitView()}
                    onElementClick={handleElementClick}
                    elements={elements}
                    nodesDraggable={false}
                    nodesConnectable={false}
                    nodeTypes={{
                        customNode: CustomNode,
                    }}
                    edgeTypes={{
                        customEdge: CustomEdge,
                    }}
                />
            </Grid>
        </Grid>
    );
};

export default V3Graph;
